import * as React from 'react';

import { useEffect, useState } from 'react';

import { CSVLink } from "react-csv";

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Code,
    Download,
    FileCode,
    Table as TableIcon,
} from 'lucide-react';

import { configuration } from '../Configuration';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { ResourceDisclosureHeader, ResourceDisclosurePanel } from './ResourceDisclosure';
import { useUrlPageState } from '../utilities/dataTablePagination';
import { userAPI } from '../UserAPI';
import { authenticationAtom } from '../State';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

/**
 * A cell value as text. Rows come straight from Mongo, so a cell may hold a reference or a
 * sub-document ({district, sheet} for landRegister, {_id, _type, label} for a relation) — React
 * throws on an object child. Label, name or title win; anything else is compact JSON.
 */
export function cellText(value: unknown, empty: string = '-'): string {
    if (value === null || value === undefined || value === '') return empty;
    if (typeof value === 'object') {
        const v = value as Record<string, unknown>;
        const label = v.label ?? v.name ?? v.title;
        if (typeof label === 'string' && label) return label;
        if (Array.isArray(value)) return value.map((x) => cellText(x, '')).filter(Boolean).join(', ') || empty;
        try { return JSON.stringify(value); } catch { return String(value); }
    }
    return String(value);
}

const baseUrl = configuration.serverUrl;

/**
 * Klickbarer Spaltenkopf — `ui/data-table` zeichnet selbst keine Sortierköpfe,
 * die abgelöste Tabelle tat es. Gleiche Semantik: erster Klick aufsteigend,
 * zweiter absteigend.
 */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: React.ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            type="button"
            className="-mx-4 h-8 max-w-full gap-1 px-4 font-medium"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{children}</span>
            {sorted === 'asc' ? (
                <ArrowUp aria-hidden className="h-3.5 w-3.5 shrink-0" />
            ) : sorted === 'desc' ? (
                <ArrowDown aria-hidden className="h-3.5 w-3.5 shrink-0" />
            ) : (
                <ArrowUpDown aria-hidden className="h-3.5 w-3.5 shrink-0 opacity-50" />
            )}
        </Button>
    );
}

/**
 * Spaltenbreiten aus den Server-Optionen. Die Zahlen sind Pixel; React hängt die
 * Einheit an. Die Breite sitzt auf dem Zellrahmen (`box-border`), damit Kopf- und
 * Datenzelle exakt gleich breit werden.
 */
const widthStyleFor = (column: any): React.CSSProperties => {
    const style: React.CSSProperties = {};
    if (column.width !== undefined && column.width !== null) style.width = column.width;
    if (column.minWidth !== undefined && column.minWidth !== null) style.minWidth = column.minWidth;
    if (column.maxWidth !== undefined && column.maxWidth !== null) style.maxWidth = column.maxWidth;
    return style;
};

// Global state for expanded resources to persist across re-renders
const expandedResources = new globalThis.Map<string, boolean>();

interface TableResourceProps {
    resource: any;
    stableKey?: string;
}

const TableResourceComponent = ({ resource, stableKey }: TableResourceProps) => {
    const { t } = useTranslation();
    const [csvData, setCSVData] = useState([] as any);
    const [columns, setColumns] = useState([] as any);
    const [items, setItems] = useState([] as any);
    const resourceKey = stableKey || resource?.reference || resource?._id;
    const [expanded, setExpanded] = useState(() => {
        const globalExpanded = expandedResources.get(resourceKey) || false;
        // TableResource initializing
        return globalExpanded;
    });
    const [authentication] = useAtom(authenticationAtom);
    const { page, onPageChange } = useUrlPageState(`tablePage_${resourceKey}`);
    const [tableResource, setTableResource] = useState(null) as any;

    useEffect(() => {
        const call = async () => {
            let newResource: any = null;

            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            if (!newResource.options) {
                newResource.options = {};
            }

            // Resource loaded

            setTableResource(newResource);

            const minimumDataLength = newResource.options.columns ? 1 : 2;

            if (!newResource.data || newResource.data.length < minimumDataLength) {
                return;
            }

            if (newResource.options.columns) {

                const mappedColumns = newResource.options.columns.map((column: any, _index: number) => {
                    // Convert width string (e.g., '200px') to number
                    // Widths are carried as numbers (pixels) throughout this component
                    let width: string | number | undefined = column.width;
                    let minWidth: string | number | undefined = column.minWidth;
                    let maxWidth: string | number | undefined = column.maxWidth;

                    // If width is provided as string (e.g., '200px'), convert to number
                    if (width && typeof width === 'string') {
                        if (width.endsWith('px')) {
                            const widthNum = parseInt(width.replace('px', ''), 10);
                            if (!isNaN(widthNum)) {
                                width = widthNum;
                            }
                        } else {
                            // Try parsing as number directly
                            const widthNum = parseInt(width, 10);
                            if (!isNaN(widthNum)) {
                                width = widthNum;
                            }
                        }
                    }

                    // Convert minWidth if it's a string
                    if (minWidth && typeof minWidth === 'string') {
                        if (minWidth.endsWith('px')) {
                            const minWidthNum = parseInt(minWidth.replace('px', ''), 10);
                            if (!isNaN(minWidthNum)) {
                                minWidth = minWidthNum;
                            }
                        } else {
                            const minWidthNum = parseInt(minWidth, 10);
                            if (!isNaN(minWidthNum)) {
                                minWidth = minWidthNum;
                            }
                        }
                    }

                    // Convert maxWidth if it's a string
                    if (maxWidth && typeof maxWidth === 'string') {
                        if (maxWidth.endsWith('px')) {
                            const maxWidthNum = parseInt(maxWidth.replace('px', ''), 10);
                            if (!isNaN(maxWidthNum)) {
                                maxWidth = maxWidthNum;
                            }
                        } else {
                            const maxWidthNum = parseInt(maxWidth, 10);
                            if (!isNaN(maxWidthNum)) {
                                maxWidth = maxWidthNum;
                            }
                        }
                    }

                    const mappedColumn: any = {
                        name: column.name,
                        selector: (item: any) => item[column.field],
                        align: column.align || 'left',
                        cell: (item: any) => {
                            switch (column.type) {
                                case 'image': {
                                    const rawValue = item[column.field];
                                    if (!rawValue) {
                                        return <span>-</span>;
                                    }
                                    const imageUrl = typeof rawValue === 'string' && rawValue.startsWith('http')
                                        ? rawValue
                                        : `${baseUrl}/${rawValue}`;
                                    return (
                                        <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                                            <img src={imageUrl} alt="" width={100} height={100} className="object-cover" />
                                        </a>
                                    );
                                }
                                case 'list': return item[column.field] ? <ul>{Object.keys(item[column.field]).map((key: string) => {
                                    return <li key={key} className='whitespace-normal'>{key}: {item[column.field][key]}</li>
                                })}</ul> : <span>-</span>
                                case 'date':
                                    return <div>{(() => {
                                        const val = item[column.field];
                                        if (!val) return '-';
                                        const d = new Date(val);
                                        return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
                                    })()}</div>;
                                default:
                                    return <div className='whitespace-normal'>{cellText(item[column.field])}</div>;
                            }
                        },
                        sortable: true,
                    };

                    // Add width properties only if they are defined
                    // Strings such as '200px' are normalised to numbers for consistency
                    if (width !== undefined && width !== null) {
                        if (typeof width === 'number') {
                            mappedColumn.width = width;
                        } else if (typeof width === 'string') {
                            // Parse string like '200px' or '200' to number
                            const numWidth = width.endsWith('px')
                                ? parseInt(width.replace('px', ''), 10)
                                : parseInt(width, 10);
                            if (!isNaN(numWidth) && numWidth > 0) {
                                mappedColumn.width = numWidth;
                            }
                        }
                    }
                    if (minWidth !== undefined && minWidth !== null) {
                        if (typeof minWidth === 'number') {
                            mappedColumn.minWidth = minWidth;
                        } else if (typeof minWidth === 'string') {
                            const numMinWidth = minWidth.endsWith('px')
                                ? parseInt(minWidth.replace('px', ''), 10)
                                : parseInt(minWidth, 10);
                            if (!isNaN(numMinWidth) && numMinWidth > 0) {
                                mappedColumn.minWidth = numMinWidth;
                            }
                        }
                    }
                    if (maxWidth !== undefined && maxWidth !== null) {
                        if (typeof maxWidth === 'number') {
                            mappedColumn.maxWidth = maxWidth;
                        } else if (typeof maxWidth === 'string') {
                            const numMaxWidth = maxWidth.endsWith('px')
                                ? parseInt(maxWidth.replace('px', ''), 10)
                                : parseInt(maxWidth, 10);
                            if (!isNaN(numMaxWidth) && numMaxWidth > 0) {
                                mappedColumn.maxWidth = numMaxWidth;
                            }
                        }
                    }


                    return mappedColumn;
                });

                // Debug: verify column widths are set
                const columnsWithWidths = mappedColumns.filter((col: any) => col.width || col.minWidth);
                if (columnsWithWidths.length > 0) {
                    console.log('TableResource columns with widths:', columnsWithWidths.map((col: any) => ({
                        name: col.name,
                        width: col.width,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth
                    })));
                }

                setColumns(mappedColumns);
            } else {
                // Ensure data[0] is an array before calling map
                if (Array.isArray(newResource.data[0])) {
                    setColumns(newResource.data[0].map((column: any, index: number) => {
                        return {
                            name: column,
                            width: 120,
                            selector: (item: any) => item[index],
                            sortable: true,
                        }
                    }));
                } else {
                    setColumns([]);
                }
            }

            // Resource options loaded

            if (newResource.options.columns) {
                setItems(newResource.data);

                // Calculate CSV data

                const csvData = [];

                if (newResource.data.length > 0) {
                    csvData.push(newResource.options.columns.map((column: any) => column.name));
                    newResource.data.forEach((object: any) => csvData.push(newResource.options.columns.map((column: any) => {
                        switch (column.type) {
                            case 'image': return object[column.field] ? `${baseUrl}/${object[column.field]}` : '';
                            case 'list': return object[column.field] ? JSON.stringify(object[column.field]).replace(/"/g, '""') : '';
                            case 'date': {
                                const val = object[column.field];
                                if (!val) return '';
                                const d = new Date(val);
                                return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
                            }
                            default: return cellText(object[column.field], '');
                        }
                    })));
                }

                setCSVData(csvData);
            } else {
                // Ensure data is an array before calling slice
                if (Array.isArray(newResource.data)) {
                    setItems(newResource.data.slice(1));
                    // For CSV, we need to ensure headers are properly formatted
                    if (newResource.data.length > 0 && Array.isArray(newResource.data[0])) {
                        setCSVData(newResource.data);
                    } else {
                        // If data[0] is not an array, create proper CSV structure
                        const csvData = [];
                        if (newResource.data.length > 0) {
                            // Create headers from the first object's keys
                            const firstItem = newResource.data[0];
                            if (typeof firstItem === 'object' && firstItem !== null) {
                                const headers = Object.keys(firstItem);
                                csvData.push(headers);
                                newResource.data.forEach((item: any) => {
                                    csvData.push(headers.map(header => item[header] || ''));
                                });
                            }
                        }
                        setCSVData(csvData);
                    }
                } else {
                    setItems([]);
                    setCSVData([]);
                }
            }
        }

        // Only call if we have a reference or if we don't have local data yet
        if (resource.reference || !tableResource) {
            call();
        }
    }, [resource.reference, resource._id, authentication.customer]);

    /**
     * Übersetzung der Server-Spalten in TanStack-Spalten für `ui/data-table`.
     * `size` trägt die Breite in den Kopf (`ui/data-table` setzt sie dort als
     * Inline-Breite), der Zellrahmen trägt sie in die Datenzeile — wie in der
     * abgelösten Tabelle, die Breite/Min/Max auf Kopf und Zelle legte.
     */
    const tableColumns: ColumnDef<any, any>[] = React.useMemo(() => (
        (columns as any[]).map((column: any, index: number) => {
            const cellStyle = widthStyleFor(column);
            const headerSize = column.width !== undefined && column.width !== null
                ? column.width
                : column.minWidth;

            return {
                id: `col-${index}`,
                accessorFn: column.selector ? (item: any) => column.selector(item) : undefined,
                enableSorting: !!column.sortable,
                ...(typeof headerSize === 'number' ? { size: headerSize } : {}),
                header: column.sortable
                    ? ({ column: tableColumn }: any) => (
                        <SortableHeader column={tableColumn}>{column.name}</SortableHeader>
                    )
                    : () => <>{column.name}</>,
                cell: column.cell
                    ? ({ row }: any) => (
                        <div style={cellStyle} className="overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2">
                            {column.cell(row.original)}
                        </div>
                    )
                    : ({ getValue }: any) => {
                        const value = getValue();

                        return (
                            <div style={cellStyle} className="overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2">
                                {value === null || value === undefined ? null : String(value)}
                            </div>
                        );
                    },
            } as ColumnDef<any, any>;
        })
    ), [columns]);

    // Convert table data to markdown format
    const convertToMarkdown = () => {
        if (!Array.isArray(csvData) || csvData.length === 0) {
            return '';
        }

        const headers = Array.isArray(csvData[0]) ? csvData[0] : [];
        const rows = csvData.slice(1);

        if (headers.length === 0) {
            return '';
        }

        // Create markdown table
        let markdown = '| ' + headers.join(' | ') + ' |\n';
        markdown += '|' + headers.map(() => '---').join('|') + '|\n';

        rows.forEach((row: any) => {
            const rowData = Array.isArray(row) ? row : [];
            markdown += '| ' + rowData.map((cell: any) => String(cell || '').replace(/\|/g, '\\|')).join(' | ') + ' |\n';
        });

        return markdown;
    };

    // Convert table data to HTML format
    const convertToHTML = () => {
        if (!Array.isArray(csvData) || csvData.length === 0) {
            return '';
        }

        const headers = Array.isArray(csvData[0]) ? csvData[0] : [];
        const rows = csvData.slice(1);

        if (headers.length === 0) {
            return '';
        }

        // Create HTML table
        let html = '<table>\n<thead>\n<tr>\n';
        headers.forEach((header: any) => {
            html += `  <th>${String(header || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</th>\n`;
        });
        html += '</tr>\n</thead>\n<tbody>\n';

        rows.forEach((row: any) => {
            const rowData = Array.isArray(row) ? row : [];
            html += '<tr>\n';
            rowData.forEach((cell: any) => {
                html += `  <td>${String(cell || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>\n`;
            });
            html += '</tr>\n';
        });

        html += '</tbody>\n</table>';

        return html;
    };

    // Copy markdown to clipboard
    const handleCopyMarkdown = async () => {
        try {
            const markdown = convertToMarkdown();
            if (markdown) {
                await navigator.clipboard.writeText(markdown);
            }
        } catch (error) {
            console.error('Failed to copy markdown:', error);
        }
    };

    // Copy HTML to clipboard
    const handleCopyHTML = async () => {
        try {
            const html = convertToHTML();
            if (html) {
                // Use the modern Clipboard API to write HTML format for Word compatibility
                if (navigator.clipboard && navigator.clipboard.write) {
                    // Create a plain text version for fallback
                    const plainText = Array.isArray(csvData) && csvData.length > 0
                        ? csvData.map((row: any) => Array.isArray(row) ? row.join('\t') : '').join('\n')
                        : '';

                    const clipboardItem = new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' })
                    });
                    await navigator.clipboard.write([clipboardItem]);
                } else {
                    // Fallback for older browsers
                    await navigator.clipboard.writeText(html);
                }
            }
        } catch (error) {
            console.error('Failed to copy HTML:', error);
        }
    };

    return <div className="box-border w-full">
        <ResourceDisclosureHeader
            expanded={expanded}
            onToggle={(e) => {
                if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                const newExpanded = !expanded;
                setExpanded(newExpanded);
                // Store in global state to persist across re-renders
                expandedResources.set(resourceKey, newExpanded);
            }}
            testId="chat-resource-table-toggle"
            icon={<TableIcon aria-hidden className="h-5 w-5 shrink-0" />}
            title={tableResource?.title}
            actions={
            <TooltipProvider delayDuration={300} skipDelayDuration={0}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            type="button"
                            data-testid="chat-resource-table-copy-markdown"
                            className="mr-1 h-8 w-8"
                            onClick={handleCopyMarkdown}
                            aria-label={t('global.copyMarkdown')}
                        >
                            <Code size={16} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('global.copyMarkdown')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            type="button"
                            data-testid="chat-resource-table-copy-html"
                            className="mr-1 h-8 w-8"
                            onClick={handleCopyHTML}
                            aria-label={t('global.copyHtml')}
                        >
                            <FileCode size={16} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('global.copyHtml')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    {/* Wrap in a span: the trigger (Radix asChild) attaches a ref +
                        listeners to its child, but CSVLink does not forward a ref to a
                        DOM node — so it must anchor to a real element. */}
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <CSVLink data={Array.isArray(csvData) && csvData.length > 1 ? csvData.slice(1) : []} headers={Array.isArray(csvData) && csvData.length > 0 && Array.isArray(csvData[0]) ? csvData[0] : []} filename={`${tableResource?.title}.csv`}>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    type="button"
                                    data-testid="chat-resource-table-download-csv"
                                    className="h-8 w-8"
                                    aria-label={t('global.downloadCsv')}
                                >
                                    <Download size={16} />
                                </Button>
                            </CSVLink>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>{t('global.downloadCsv')}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            }
        />
        <ResourceDisclosurePanel expanded={expanded && !!tableResource}>
            {/* `tableScrollWrapper` bleibt: App.sass hebt im Chat für genau diese
                Klasse das globale `overflow-x: hidden` auf, damit breite Tabellen
                waagerecht scrollen. `overflow-x-auto` trägt dasselbe außerhalb des Chats. */}
            <div
                data-testid="chat-resource-table"
                className="tableScrollWrapper relative block w-full min-w-0 max-w-full overflow-x-auto"
            >
                <DataTable
                    data={items}
                    columns={tableColumns}
                    page={page}
                    onPageChange={onPageChange}
                    rowClassName={() => '[&>td]:p-0'}
                    emptyMessage={t('settingsPanel.dataTable.noRecordsText')}
                    previousAriaLabel={t('settingsPanel.dataTable.previousText')}
                    nextAriaLabel={t('settingsPanel.dataTable.nextText')}
                />
            </div>
        </ResourceDisclosurePanel>
    </div>
};

// Export with memoization but include data and options in the check
export const TableResource = React.memo(TableResourceComponent, (prevProps, nextProps) => {
    // Custom comparison function for memoization
    return (
        prevProps.stableKey === nextProps.stableKey &&
        prevProps.resource?.reference === nextProps.resource?.reference &&
        prevProps.resource?._id === nextProps.resource?._id &&
        prevProps.resource?.data === nextProps.resource?.data &&
        prevProps.resource?.options === nextProps.resource?.options
    );
});
